import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, SvgIcon, Tooltip } from '@mui/material';
import { Search } from '@mui/icons-material';
import LocalShipping from '@mui/icons-material/LocalShipping';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import AccessTime from '@mui/icons-material/AccessTime';
import LinkOff from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { client, MarketSupplier, MarketSupplierFilterOptions } from '../../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../../components/shared/useUser';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableColumn, TableSectionProps } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';

type Props = {
  onCreateNew: () => void;
  onFilterChange?: (_instance: string, _market: string, searchTerm?: string) => void;
  initialInstance?: string;
  initialMarket?: string;
  initialSearchTerm?: string;
  /** Splits filters, tiles and table so the caller can slot them into ListPageTemplate. */
  children: (parts: {
    filterBar: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<MarketSupplier>;
    isInitialLoading: boolean;
  }) => ReactNode;
};

const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Cut off time is stored as a plain HH:mm plus a separate timezone offset - adjust and label it for display.
const formatCutOffTime = (supplier: MarketSupplier) => {
  if (!supplier.cutOffTime) return '-';

  if (supplier.timezoneOffset !== undefined && supplier.timezoneOffset !== null) {
    const [hours, minutes] = supplier.cutOffTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + Number(supplier.timezoneOffset) * 60;
    const adjustedTotalMinutes = ((totalMinutes % 1440) + 1440) % 1440;
    const adjustedHours = Math.floor(adjustedTotalMinutes / 60);
    const adjustedMinutes = adjustedTotalMinutes % 60;
    const formatted = `${String(adjustedHours).padStart(2, '0')}:${String(adjustedMinutes).padStart(2, '0')}`;
    return supplier.timezone ? `${formatted} (${supplier.timezone})` : formatted;
  }

  return supplier.timezone ? `${supplier.cutOffTime} (${supplier.timezone})` : supplier.cutOffTime;
};

const SuppliersListPageContent = ({ onCreateNew, onFilterChange, initialInstance, initialMarket, initialSearchTerm, children }: Props) => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermissionTo } = useContext(UserContext);
  const canEdit = hasPermissionTo(['ConfigurationAdmin']) || hasPermissionTo(['SupplierUpdate']);

  const [list, setList] = useState<MarketSupplier[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const { instances, getMarkets, ensureMarketsLoaded, isMarketsLoading, defaultFiltersLoading } = useInstanceMarket();
  const markets = getMarkets(selectedInstance);
  const pageSize = 20;

  // Headline tiles summarise the whole Instance/Market scope (not the current search), fetched once per scope change so they don't slow down search/paging.
  const [statsList, setStatsList] = useState<MarketSupplier[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(false);
  const [suppliedCatalogueSupplierIds, setSuppliedCatalogueSupplierIds] = useState<Set<number>>(new Set());
  const [isCatalogueLinkLoading, setIsCatalogueLinkLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedInstance) {
      setStatsList([]);
      setSuppliedCatalogueSupplierIds(new Set());
      return;
    }

    setIsStatsLoading(true);
    client.getMarketSuppliers({
      companyId: selectedInstance === 'all' ? undefined : selectedInstance,
      customerTypeId: selectedMarket || undefined,
      pageNumber: 0,
      pageSize: 2147483647,
      sort: 'supplierName',
      direction: 'asc',
    })
      .then((result) => {
        if (result.success && result.data) {
          setStatsList(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching supplier stats:', error))
      .finally(() => setIsStatsLoading(false));

    setIsCatalogueLinkLoading(true);
    client.getMarketCatalogues({
      companyId: selectedInstance === 'all' ? undefined : selectedInstance,
      customerTypeId: selectedMarket || undefined,
      pageNumber: 0,
      pageSize: 2147483647,
    })
      .then((result) => {
        if (result.success && result.data) {
          const ids = new Set<number>();
          (result.data.items ?? []).forEach((catalogue) => {
            (catalogue.supplierIds ?? []).forEach((id) => ids.add(id));
          });
          setSuppliedCatalogueSupplierIds(ids);
        }
      })
      .catch((error) => console.error('Error fetching catalogue links for supplier stats:', error))
      .finally(() => setIsCatalogueLinkLoading(false));
  }, [selectedInstance, selectedMarket]);

  const missingFileDeliverySetupCount = useMemo(() => (
    statsList.filter((supplier) => !supplier.fileFormat || !supplier.publishMethod).length
  ), [statsList]);

  const missingCutoffCount = useMemo(() => (
    statsList.filter((supplier) => !supplier.cutOffTime).length
  ), [statsList]);

  const notLinkedToCatalogueCount = useMemo(() => (
    statsList.filter((supplier) => !suppliedCatalogueSupplierIds.has(supplier.supplierId)).length
  ), [statsList, suppliedCatalogueSupplierIds]);

  const fetchSuppliers = async () => {
    setIsLoading(true);
    try {
      const options: MarketSupplierFilterOptions = {
        companyId: selectedInstance || undefined,
        customerTypeId: selectedMarket || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
        sort: 'supplierName',
        direction: 'asc',
      };
      const result = await client.getMarketSuppliers(options);
      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      } else {
        setList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
  }, [initialInstance, initialMarket, initialSearchTerm]);

  useEffect(() => {
    if (selectedInstance) {
      ensureMarketsLoaded(selectedInstance);
    } else {
      setSelectedMarket('');
    }
  }, [selectedInstance, ensureMarketsLoaded]);

  useEffect(() => {
    if (isMarketsLoading(selectedInstance)) return;

    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      if (selectedMarket !== onlyMarket) {
        setSelectedMarket(onlyMarket);
        onFilterChange?.(selectedInstance, onlyMarket, searchTerm);
        return;
      }
    }

    if (selectedInstance || selectedInstance === 'all')
    {
      fetchSuppliers();
    }

  }, [markets, currentPage, searchTerm, selectedInstance, selectedMarket]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchInput);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket(''); // Reset market when instance changes
    setCurrentPage(0);
    onFilterChange?.(event.target.value, '');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, event.target.value);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('all');
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.('all', '', '');
  };

  const handleRowClick = (supplier: MarketSupplier) => {
    if (!canEdit) return;
    const params = new URLSearchParams();
    ['companyId', 'customerTypeId', 'searchTerm'].forEach((key) => {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    });
    navigate(`/procurement/suppliers/edit/${supplier.supplierId}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const columns: TableColumn<MarketSupplier>[] = [
    { id: 'supplierNumber', label: t('supplierNumber'), field: 'supplierNumber' },
    {
      id: 'supplierName',
      label: t('supplierName'),
      render: (row) => (
        <Tooltip title={row.supplierName}
          placement="top">
          <span>{truncateText(row.supplierName)}</span>
        </Tooltip>
      ),
    },
    {
      id: 'emailAddress',
      label: t('emailAddress'),
      render: (row) => (
        <Tooltip title={row.emailAddress}
          placement="top">
          <span>{truncateText(row.emailAddress)}</span>
        </Tooltip>
      ),
    },
    { id: 'fileFormat', label: t('fileFormat'), field: 'fileFormat' },
    { id: 'publishMethod', label: t('publishMethod'), field: 'publishMethod' },
    {
      id: 'cutOffTime',
      label: t('cutOffTime'),
      align: 'center',
      render: (row) => formatCutOffTime(row),
    },
    {
      id: 'instanceName',
      label: t('instanceName'),
      render: (row) => (
        <Tooltip title={row.instanceName}
          placement="top">
          <span>{truncateText(row.instanceName)}</span>
        </Tooltip>
      ),
    },
    {
      id: 'marketName',
      label: t('marketName'),
      render: (row) => (
        <Tooltip title={row.marketName || 'All Markets'}
          placement="top">
          <span>{truncateText(row.marketName || 'All Markets')}</span>
        </Tooltip>
      ),
    },
  ];

  const table: TableSectionProps<MarketSupplier> = {
    title: t('suppliers'),
    columns,
    rows: list,
    getRowId: (row) => row.supplierId,
    onRowClick: canEdit ? handleRowClick : undefined,
    recordCount: totalCount,
    recordCountLabel: t('suppliers'),
    loading: isLoading,
    emptyContent: (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('suppliers')}
        searchTerm={searchInput}
        onClear={handleClear}
        onAdd={onCreateNew}
        onAddIsCreating={false}
        addItemText={t('createNew')}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    onPageChange: handlePageChange,
  };

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          {t('clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByInstance')}
          </InputLabel>
          <Select
            value={instances.some(m => m.id?.toString() == selectedInstance) ? selectedInstance : ''}
            onChange={handleInstanceChange}
            label={t('filterByInstance')}
            displayEmpty
            notched
            renderValue={(value) => {
              if (value === '') {
                return <span>{t('allInstances')}</span>;
              }

              return instances?.find(i => i.id.toString() === value)?.name || '';
            }}
          >
            <MenuItem value="all">
              {t('allInstances')}
            </MenuItem>
            {instances.map((instance) => (
              <MenuItem
                key={instance.id}
                value={instance.id.toString()}
              >
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByMarket')}
          </InputLabel>
          <Select
            value={markets.some(m => m.customerTypeId?.toString() == selectedMarket) ? selectedMarket : ''}
            onChange={handleMarketChange}
            label={t('filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            <MenuItem value="">
              {t('allMarkets')}
            </MenuItem>
            {markets.map((market) => (
              <MenuItem
                key={market.customerTypeId}
                value={market.customerTypeId.toString()}
              >
                {market.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile
        key="totalSuppliers"
        label={t('tileTotalSuppliers', 'Total Suppliers')}
        value={statsList.length}
        description={t('tileTotalSuppliersDescription', 'All suppliers in the selected Instance/Market')}
        icon={<LocalShipping />}
        variant="primary"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="missingFileDeliverySetup"
        label={t('tileMissingFileDelivery', 'Missing File Delivery Setup')}
        value={missingFileDeliverySetupCount}
        description={t('tileMissingFileDeliveryDescription', 'Suppliers with no file format or publish method - orders can\'t be sent')}
        icon={<InsertDriveFile />}
        variant="error"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="missingCutoff"
        label={t('tileMissingCutoff', 'Missing Cutoff Time')}
        value={missingCutoffCount}
        description={t('tileMissingCutoffDescription', 'Suppliers with no order cutoff time configured')}
        icon={<AccessTime />}
        variant="warning"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="notLinkedToCatalogue"
        label={t('tileNotLinkedToCatalogue', 'Not Linked to Any Catalogue')}
        value={notLinkedToCatalogueCount}
        description={t('tileNotLinkedToCatalogueDescription', 'Suppliers not used by any catalogue - unreachable for DSD ordering')}
        icon={<LinkOff />}
        variant="secondary"
        loading={isStatsLoading || isCatalogueLinkLoading}
      />,
    ],
  };

  // Still resolving the default instance/market before the first fetch even starts - keep showing the skeleton, not a zero-data flash.
  return <>{children({ filterBar, headlineContent, table, isInitialLoading: (defaultFiltersLoading || isLoading) && !hasLoadedOnce })}</>;
};

export default SuppliersListPageContent;
